// BrokerSidebar.js
// Sleek sidebar with profile, Dashboard / Projects / Intake / Profile, and footer actions.
// Mobile drawer mirrors desktop.
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LogOut,
  ClipboardList,
  PlaneTakeoff,
  LayoutDashboard,
  User as UserIcon,
  Menu,
  X,
  HelpCircle,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import strobeLogo from "../assets/images/strobelogo.svg";
import BrokerHelpPopup from "./BrokerHelpPopup";
// Optional: analytics hook
import { trackClickEvent } from "../utils/analytics";
import { clearCachedUserAccess } from "../utils/authRoles";
import { withTimeout } from "../utils/asyncTimeout";
import { traceAsync } from "../utils/perfTrace";
import { formatDisplayName } from "../utils/displayName";

// --- small helpers ---
function initialsFrom(user, profileName = "") {
  const nameParts = profileName.trim().split(/\s+/).filter(Boolean);
  if (nameParts.length > 0) {
    const firstInitial = nameParts[0]?.[0] || "";
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] || "" : "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }

  const meta = user?.user_metadata || {};
  const f = (meta.first_name || meta.firstName || "").trim();
  const l = (meta.last_name || meta.lastName || "").trim();
  if (f || l) return `${f?.[0] || ""}${l?.[0] || ""}`.toUpperCase();
  const email = user?.email || "";
  return email ? email[0].toUpperCase() : "B";
}

function displayNameFrom(user, profileName = "") {
  if (profileName) return formatDisplayName(profileName);

  const meta = user?.user_metadata || {};
  const first = (meta.first_name || meta.firstName || "").trim();
  const last = (meta.last_name || meta.lastName || "").trim();
  if (first || last) return formatDisplayName(`${first} ${last}`.trim());
  // fallback: email; if none, "Broker"
  return user?.email || "Broker";
}

function rolePill(roleOverride, brokerRole) {
  if (roleOverride) return roleOverride.toUpperCase();
  if (brokerRole === "assistant") return "TEAM MEMBER";
  return "BROKER";
}

const BROKER_IDENTITY_CACHE_KEY = "strobe.brokerSidebarIdentity";

function readCachedIdentity() {
  try {
    return JSON.parse(sessionStorage.getItem(BROKER_IDENTITY_CACHE_KEY) || "null");
  } catch {
    return null;
  }
}

function cacheIdentity(identity) {
  if (!identity) {
    sessionStorage.removeItem(BROKER_IDENTITY_CACHE_KEY);
    return;
  }

  sessionStorage.setItem(BROKER_IDENTITY_CACHE_KEY, JSON.stringify(identity));
}

export default function Sidebar({ roleOverride }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [profileName, setProfileName] = useState("");
  const [brokerRole, setBrokerRole] = useState("");
  const [cachedIdentity, setCachedIdentity] = useState(() => readCachedIdentity());

  const loadBrokerIdentity = async (nextUser) => {
    if (!nextUser?.id) {
      cacheIdentity(null);
      setCachedIdentity(null);
      setUser(null);
      setProfileName("");
      setBrokerRole("");
      return;
    }

    const [{ data }, { data: membership }] = await Promise.all([
      traceAsync(
        "sidebar.broker.profile",
        () => supabase
          .from("broker_profiles")
          .select("first_name, last_name")
          .eq("user_id", nextUser.id)
          .maybeSingle(),
        {}
      ),
      traceAsync(
        "sidebar.broker.membership",
        () => supabase
          .from("broker_account_members")
          .select("role")
          .eq("user_id", nextUser.id)
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle(),
        {}
      ),
    ]);

    const fullName = `${data?.first_name || ""} ${data?.last_name || ""}`.trim();
    const nextRole = membership?.role || "";
    const nextIdentity = {
      displayName: displayNameFrom(nextUser, fullName),
      initials: initialsFrom(nextUser, fullName),
      avatarUrl: nextUser.user_metadata?.avatar_url || "",
      pillText: rolePill(roleOverride, nextRole),
    };

    cacheIdentity(nextIdentity);
    setCachedIdentity(nextIdentity);
    setUser(nextUser);
    setProfileName(fullName);
    setBrokerRole(nextRole);
  };

  // Load user once; keep fresh on auth changes
  useEffect(() => {
    let mounted = true;

    (async () => {
      const { data } = await traceAsync("sidebar.broker.getUser", () => supabase.auth.getUser(), {});
      if (mounted) await loadBrokerIdentity(data?.user || null);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        loadBrokerIdentity(null);
        return;
      }

      if (session?.user) {
        loadBrokerIdentity(session.user);
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const navItems = useMemo(
    () => [
      { label: "Dashboard", path: "/portal", icon: LayoutDashboard },
      { label: "Projects", path: "/brokerprojects", icon: PlaneTakeoff },
      { label: "Profile", path: "/profile", icon: UserIcon },
    ],
    []
  );

  // derived display bits
  const avatarInitials = user ? initialsFrom(user, profileName) : cachedIdentity?.initials || "";
  const displayName = formatDisplayName(user ? displayNameFrom(user, profileName) : cachedIdentity?.displayName || "");
  const avatarUrl = user?.user_metadata?.avatar_url || cachedIdentity?.avatarUrl || "";
  const pillText = brokerRole || roleOverride ? rolePill(roleOverride, brokerRole) : cachedIdentity?.pillText || "";

  const handleLogout = async () => {
    try {
      await withTimeout(supabase.auth.signOut(), 6000, "Sign out timed out");
    } catch (error) {
      console.warn("Broker sign out did not finish cleanly:", error?.message || error);
    } finally {
      clearCachedUserAccess();
      cacheIdentity(null);
      trackClickEvent?.("sidebar_logout_click");
      navigate("/login", { replace: true });
    }
  };

  const NavLinks = ({ onClick }) => (
    <nav className="space-y-2">
       {/* New Intake CTA */}
<Link
  to="/intake"
  onClick={() => trackClickEvent?.("sidebar_new_intake_click")}
  className="inline-flex items-center justify-center w-full mt-3
             rounded-lg border border-white/30 bg-strobe-red/100 hover:bg-strobe-red/20
             backdrop-blur-md text-white text-base font-medium font-medium px-3 py-2
             transition duration-300"
>
  + New Request
</Link>
      
      {navItems.map(({ label, path, icon: Icon }) => {
        const active = pathname.startsWith(path);
        return (
          <Link
            key={label}
            to={path}
            onClick={() => {
              onClick?.();
              trackClickEvent?.("sidebar_link_click", { label, path });
            }}
            className={`group flex items-center gap-3 rounded-lg px-3 py-2 transition
              ${active ? "bg-white/10 text-white border-l-4 border-white" : "text-white/90 hover:text-white hover:bg-white/5"}
            `}
            aria-current={active ? "page" : undefined}
          >
            <Icon className="w-5 h-5 shrink-0" />
            <span className="truncate">{label}</span>
          </Link>
        );
      })}
    </nav>
  );

const handleAvatarUpload = async (event) => {
  const file = event.target.files?.[0];
  if (!file || !user) return;

  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}-${Date.now()}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;

    // Upload (overwrite if exists)
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);
    const publicUrl = urlData.publicUrl;

    // Update auth profile
    const { error: updateError } = await supabase.auth.updateUser({
      data: { avatar_url: publicUrl }
    });

    if (updateError) throw updateError;

    // Update local state so UI refreshes Urgently
    setUser((prev) => ({
      ...prev,
      user_metadata: {
        ...prev?.user_metadata,
        avatar_url: publicUrl
      }
    }));
    const nextIdentity = {
      displayName,
      initials: avatarInitials,
      avatarUrl: publicUrl,
      pillText,
    };
    cacheIdentity(nextIdentity);
    setCachedIdentity(nextIdentity);

    console.log("✅ Avatar updated:", publicUrl);
  } catch (err) {
    console.error("❌ Error uploading avatar:", err.message);
  }
};



const ProfileBlock = () => {
  return (
    <div className="mb-4">
      <div className="flex flex-col items-center text-center group">
        {/* avatar with edit overlay */}
        <div className="relative w-20 h-20">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={`${displayName} avatar`}
              className="w-20 h-20 rounded-full object-cover ring-2 ring-white/15"
            />
          ) : (
            <div
              aria-label="User avatar"
              className="w-20 h-20 rounded-full bg-white/15 text-white flex items-center justify-center text-lg font-bold ring-2 ring-white/10"
            >
              {avatarInitials}
            </div>
          )}

          {/* Edit button overlay */}
          <label className="absolute bottom-0 right-0 bg-black/60 hover:bg-black p-1 rounded-full cursor-pointer">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarUpload} // now works
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.293-6.293a1 1 0 011.414 0l.586.586a1 1 0 010 1.414L11 15l-4 1 1-4z" />
            </svg>
          </label>
        </div>

        {/* name */}
        <div className="mt-3">
          <p className="text-base md:text-lg font-semibold leading-tight truncate max-w-[12rem]">
            {displayName || "\u00a0"}
          </p>
        </div>

        {/* role pill */}
        <div className={`mt-2 flex justify-center ${pillText ? "" : "invisible"}`}>
          <span
            className="inline-flex items-center rounded-md bg-white text-[#1A1A1A]
               px-2.5 py-0.5 text-[12px] font-semibold tracking-wide
               shadow-sm ring-1 ring-black/5"
          >
            {pillText}
          </span>
        </div>
      </div>
    </div>
  );
};



  return (
    <>
      {/* DESKTOP SIDEBAR */}
      <aside className="hidden md:fixed md:flex w-64 h-screen flex-col justify-between bg-black text-white p-6">
        {/* Top: Logo + Profile + Nav */}
        <div>
          <Link 
  to="/portal" 
  className="mb-8 flex justify-center" 
  aria-label="Go to dashboard"
>
  <img src={strobeLogo} alt="Strobe Aviation" className="w-40" />
</Link>

<div className="mt-6">
  <ProfileBlock />
</div>

<div className="mt-6">
  <NavLinks />
</div>

        </div>

        {/* Footer with separator */}
        <div className="pt-6 border-t border-white/20 space-y-3">
          <button
            type="button"
            className="flex items-center gap-3 text-sm text-white/90 hover:text-white"
            onClick={() => {
              trackClickEvent?.("sidebar_help_click");
              setHelpOpen(true);
            }}
          >
            <HelpCircle className="w-4 h-4" />
            Help
          </button>

          <button
            onClick={handleLogout}
            className="flex items-center gap-3 text-sm hover:text-white"
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* MOBILE TRIGGER */}
      {!isOpen && (
        <button
          onClick={() => {
            setIsOpen(true);
            trackClickEvent?.("sidebar_toggle_open");
          }}
          className="md:hidden fixed top-4 right-4 z-50 text-[#d3222a]"
          aria-label="Open Menu"
          aria-expanded="false"
        >
          <Menu size={28} />
        </button>
      )}

  {/* MOBILE DRAWER (mirrors desktop) */}
{isOpen && (
  <div className="fixed top-0 left-0 w-64 h-full bg-black text-white z-40 p-6 flex flex-col">

    {/* Close button - now separate from logo */}
    <button
      onClick={() => {
        setIsOpen(false);
        trackClickEvent?.("sidebar_toggle_close");
      }}
      aria-label="Close Menu"
      aria-expanded="true"
      className="absolute top-6 right-6"
    >
      <X size={24} className="text-white" />
    </button>

    {/* Logo now sits below X */}
    <div className="flex justify-center w-full mt-10 mb-6">
      <Link
        to="/portal"
        onClick={() => {
          setIsOpen(false);
          trackClickEvent?.("sidebar_logo_click_mobile");
        }}
      >
        <img src={strobeLogo} alt="Strobe Aviation" className="w-40" />
      </Link>
    </div>

    <div className="mt-6">
      <ProfileBlock />
    </div>

    <div className="mt-6">
      <NavLinks onClick={() => setIsOpen(false)} />
    </div>


          {/* Footer */}
          <div className="pt-6 border-t border-white/20 mt-auto space-y-3">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                setHelpOpen(true);
                trackClickEvent?.("sidebar_help_click_mobile");
              }}
              className="flex items-center gap-3 text-sm text-white/90 hover:text-white"
            >
              <HelpCircle className="w-4 h-4" />
              Help
            </button>

            <button
              onClick={async () => {
                await handleLogout();
                setIsOpen(false);
              }}
              className="flex items-center gap-3 text-sm hover:text-white"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
      <BrokerHelpPopup open={helpOpen} onOpenChange={setHelpOpen} renderButton={() => null} />
    </>
  );
}
